import type { PropsWithChildren } from 'react';
import React, { useCallback } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsListener } from '@atlaskit/analytics-next';

import type { ProductEdition, SearchExperimentLayer } from '@atlassian/search-experiment';

type ExperimentLayersAnalyticsListenerProps = {
	experimentLayers?: SearchExperimentLayer[];
	productEdition?: ProductEdition;
};

/**
 * This analytics listener mutates searchResults shown postQuerySearchResults
 * analytics events fired from Quick Search. It replaces the abTest attribute
 * with the backendExperiments attribute.
 *
 * This code exists here rather than in platform code as this logic is specific
 * to the Confluence product.
 */
export const ExperimentLayersAnalyticsListener = ({
	experimentLayers,
	productEdition,
	children,
}: PropsWithChildren<ExperimentLayersAnalyticsListenerProps>) => {
	const handleEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			const action = event.payload.action;
			const actionSubject = event.payload.actionSubject;
			const actionSubjectId = event.payload.actionSubjectId;
			const isQuickFind = event.payload.attributes?.isQuickFind;

			// Do not update the event if its not a searchResults shown event on Quick Search
			if (
				actionSubject !== 'searchResults' ||
				action !== 'shown' ||
				actionSubjectId !== 'postQuerySearchResults' ||
				isQuickFind
			) {
				return;
			}

			event.update((payload) => {
				return {
					...payload,
					attributes: {
						...payload.attributes,
						backendExperiments: {
							experimentLayers,
						},
						productEdition,
						abTest: undefined,
					},
				};
			});
		},
		[experimentLayers, productEdition],
	);

	return (
		<AnalyticsListener onEvent={handleEvent} channel={FabricChannel.elements}>
			{children}
		</AnalyticsListener>
	);
};
